import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import onlineOrderingService from "../services/onlineOrderingService";
import { floorLoyaltyPoints } from "../utils/rewardUtils";

export const fetchLoyaltyStatus = createAsyncThunk(
    "loyalty/fetchStatus",
    async ({ restaurantSlug, locationId = null, committedPoints = 0 }, { rejectWithValue }) => {
        const result = await onlineOrderingService.getCustomerLoyaltyStatus(
            restaurantSlug,
            locationId,
            committedPoints
        );
        if (!result.success) {
            return rejectWithValue(result.message || "Failed to load loyalty status");
        }
        return result.data;
    }
);

export const fetchAvailableRewards = createAsyncThunk(
    "loyalty/fetchRewards",
    async ({ restaurantSlug, locationId = null, committedPoints = 0 }, { rejectWithValue }) => {
        const result = await onlineOrderingService.getAvailableRewards(
            restaurantSlug,
            locationId,
            committedPoints
        );
        if (!result.success) {
            return rejectWithValue(result.message || "Failed to load rewards");
        }
        return result.data;
    }
);

const initialState = {
    currentPoints: 0,
    pendingOrderPoints: 0,
    committedPoints: 0,
    remainingPoints: 0,
    customerLoyaltyEnabled: false,
    loyaltyProgramActive: false,
    earnRuleSummary: "",
    rewards: [],
    loading: false,
    rewardsLoading: false,
    error: null,
    lastRestaurantSlug: null,
};

const loyaltySlice = createSlice({
    name: "loyalty",
    initialState,
    reducers: {
        setCommittedReward: (state, action) => {
            const required = floorLoyaltyPoints(
                action.payload?.requiredPoints ?? action.payload?.reward?.requiredPoints ?? 0
            );
            state.committedPoints = required;
            state.remainingPoints = Math.max(0, state.currentPoints - required);
        },
        clearCommittedReward: (state) => {
            state.committedPoints = 0;
            state.remainingPoints = state.currentPoints;
        },
        resetLoyalty: () => ({ ...initialState }),
        updatePointsFromValidation: (state, action) => {
            const data = action.payload || {};
            if (data.currentPoints !== undefined) {
                state.currentPoints = floorLoyaltyPoints(data.currentPoints);
            }
            if (data.committedPoints !== undefined) {
                state.committedPoints = floorLoyaltyPoints(data.committedPoints);
            }
            if (data.remainingPoints !== undefined) {
                state.remainingPoints = floorLoyaltyPoints(data.remainingPoints);
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchLoyaltyStatus.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchLoyaltyStatus.fulfilled, (state, action) => {
                state.loading = false;
                const data = action.payload || {};
                state.currentPoints = floorLoyaltyPoints(data.currentPoints);
                state.pendingOrderPoints = floorLoyaltyPoints(data.pendingOrderPoints ?? data.PendingOrderPoints);
                state.committedPoints = floorLoyaltyPoints(data.committedPoints);
                state.remainingPoints = floorLoyaltyPoints(data.remainingPoints);
                state.customerLoyaltyEnabled = !!data.customerLoyaltyEnabled;
                state.loyaltyProgramActive = !!data.loyaltyProgramActive;
                state.earnRuleSummary = data.earnRuleSummary || "";
                state.lastRestaurantSlug = action.meta?.arg?.restaurantSlug || state.lastRestaurantSlug;
            })
            .addCase(fetchLoyaltyStatus.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || action.error?.message || "Failed to load loyalty";
            })
            .addCase(fetchAvailableRewards.pending, (state) => {
                state.rewardsLoading = true;
            })
            .addCase(fetchAvailableRewards.fulfilled, (state, action) => {
                state.rewardsLoading = false;
                const data = action.payload || {};
                state.currentPoints = floorLoyaltyPoints(data.currentPoints);
                state.pendingOrderPoints = floorLoyaltyPoints(data.pendingOrderPoints ?? data.PendingOrderPoints);
                state.committedPoints = floorLoyaltyPoints(data.committedPoints);
                state.remainingPoints = floorLoyaltyPoints(data.remainingPoints);
                state.customerLoyaltyEnabled = !!data.customerLoyaltyEnabled;
                state.loyaltyProgramActive = !!data.loyaltyProgramActive;
                state.rewards = Array.isArray(data.rewards) ? data.rewards : [];
            })
            .addCase(fetchAvailableRewards.rejected, (state) => {
                state.rewardsLoading = false;
                state.rewards = [];
            });
    },
});

export const {
    setCommittedReward,
    clearCommittedReward,
    resetLoyalty,
    updatePointsFromValidation,
} = loyaltySlice.actions;

export const selectLoyalty = (state) => state.loyalty;
export const selectCanUseLoyalty = (state) =>
    state.loyalty.customerLoyaltyEnabled && state.loyalty.loyaltyProgramActive;

export default loyaltySlice.reducer;
